// @flow
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import { createAction } from 'redux-act';

import { updateProducts, type Product } from '@gandi/cart';
import {
  contactbook,
  type FavoriteContact,
} from '@gandi/redux-resource-contactbook';

import type { Dispatch, GetState } from 'src/types';

import { getOwnerContactExtraParams } from '../../DomainContacts/Wizard/modules/actions';
import { selectDomainOwnerId } from '../../modules/selectors';
import type { CreateOrTransfer, EditableRole } from '../types';

import {
  selectCurrentContactByRoles,
  selectContactsWithSameId,
  selectDomainCreateAndTransfer,
  selectDomainCreate,
  selectDomainTransfer,
  selectSetupOwner,
  selectShouldFetchFavoriteContacts,
  selectShouldFetchOneContactExtraParams,
} from './selectors';

export type SaveDraftContactParams = {
  process: CreateOrTransfer,
  contact: FavoriteContact,
  role: EditableRole,
};
type SaveDraftContactProps = {
  process: CreateOrTransfer,
  contact: FavoriteContact,
  role: EditableRole,
};

export type SaveDraftContactAction = SaveDraftContactParams => void;

export const saveOneDraftContact: {
  (
    ...| Array<SaveDraftContactProps>
    | Array<{ contact: FavoriteContact, process: string, role: string }>
  ): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[Shop] Save one draft contact');

export const cleanUpAllDrafts: {
  (...mixed): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[Shop] Clean up all drafts');

export const saveDraftContact =
  (
    props: SaveDraftContactProps,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<any>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const processes = ['create', 'transfer'];

    if (props.contact.id) {
      const contactsWithSameId = selectContactsWithSameId(
        getState(),
        props.contact.id,
      );

      // TODO: optimize this part to call one grouped action
      // if all contacts are based on same favorite contact
      // it makes 6 state changes
      processes.forEach(process => {
        const contact = contactsWithSameId[process];
        if (contact) {
          const roles = ['admin', 'bill', 'tech'];
          roles.forEach(role => {
            if (contact[role]) {
              dispatch(
                saveOneDraftContact({ process, role, contact: props.contact }),
              );
            }
          });
        }
      });
    }
    return dispatch(saveOneDraftContact(props));
  };

export const loadFavoriteContacts =
  (
    contactbookOrgId: string,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    // grab favorite contacts to set contacts
    if (selectShouldFetchFavoriteContacts(getState(), contactbookOrgId)) {
      await dispatch(contactbook.fetchContactsWithRole(contactbookOrgId));
    }

    // Load the extra params for the contacts with role for the contactbookOrgId.
    // We load them for all the TLDs at once, it might be cheaper than to do a call for each TLD.
    const defaultContacts: Array<FavoriteContact> = (Object.values(
      contactbook.selectContactByRole(getState(), contactbookOrgId),
    ): any);
    await Promise.allSettled(
      defaultContacts
        .filter(contact =>
          selectShouldFetchOneContactExtraParams(getState(), {
            orgId: contactbookOrgId,
            contactId: contact.id,
          }),
        )
        .map(contact =>
          dispatch(
            contactbook.fetchContactExtraParams(contactbookOrgId, contact.id),
          ),
        ),
    );

    return Promise.resolve();
  };

const getDomainsWithUpdatedPayload =
  (props: {
    owner: FavoriteContact,
    create: boolean,
    transfer: boolean,
  }): ((Dispatch, GetState) => Promise<Array<Product>> | Promise<Array<any>>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    // create and transfer | create | transfer
    let domainsToPatch = selectDomainCreateAndTransfer(getState());
    if (props.create && !props.transfer) {
      domainsToPatch = selectDomainCreate(getState());
    }
    if (props.transfer && !props.create) {
      domainsToPatch = selectDomainTransfer(getState());
    }

    // if no products to patch, exit
    if (domainsToPatch.length === 0) {
      return [];
    }
    let ownerContactExtraParams = {};

    // grab favorite contacts to set contacts
    const contactbookOrgId = selectSetupOwner(getState());
    ownerContactExtraParams = await dispatch(
      getOwnerContactExtraParams(
        contactbookOrgId,
        // $FlowFixMe refine @gandi/cart ProductPayload
        domainsToPatch.map(product => product.payload.tld),
      ),
    );

    await loadFavoriteContacts(contactbookOrgId);

    // split transfer and create because of favorite contacts
    const contactsCreate = selectCurrentContactByRoles(getState(), 'create');
    const contactsTransfer = selectCurrentContactByRoles(
      getState(),
      'transfer',
    );

    // $FlowFixMe[missing-type-arg]
    const productsData = domainsToPatch.map<Product>(product => {
      // merge the steps product payload and the existing product payload
      const cleanPayload = omit(product.payload, ['trustee', 'trustee_roles']);
      const contacts =
        product.process === 'create' ? contactsCreate : contactsTransfer;

      const contactsWithExtraParams = mapValues(contacts, (contact, role) =>
        contact?.id
          ? {
              ...contact,
              extra_parameters:
                contactbook.selectContactExtraParams(
                  getState(),
                  contactbookOrgId,
                  contact.id,
                  // $FlowFixMe refine @gandi/cart ProductPayload
                )?.[product.payload.tld]?.[role] ?? {},
            }
          : contact,
      );

      const newPayload = {
        ...cleanPayload,
        ...contactsWithExtraParams,
        owner: {
          ...props.owner,
          extra_parameters: {
            // $FlowFixMe refine @gandi/cart ProductPayload
            ...(ownerContactExtraParams[product.payload.tld] || {}),
          },
        },
        lastCurrentOrgaId: selectDomainOwnerId(getState()),
      };
      return { ...product, payload: newPayload };
    });

    return productsData;
  };

export type UpdateDomainsPayloadProps = {
  owner: FavoriteContact,
  create: boolean,
  transfer: boolean,
};

export const updateDomainsPayload =
  (props: UpdateDomainsPayloadProps): (Dispatch => Promise<void>) =>
  async dispatch => {
    // Retrieve the domains updated contacts payload
    const updatedContactsProducts = await dispatch(
      getDomainsWithUpdatedPayload({
        owner: props.owner,
        create: props.create,
        transfer: props.transfer,
      }),
    );

    // PATCH any updated products API-side.
    return updatedContactsProducts.length
      ? dispatch(
          updateProducts({
            productsData: updatedContactsProducts,
            options: {
              headers: { 'X-Dry-Run': 1 },
            },
          }),
        )
      : Promise.resolve();
  };
